import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { ORIENTATION_CATEGORIES } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

interface Item {
  id?: string
  name: string
  category: (typeof ORIENTATION_CATEGORIES)[number]
  isNew?: boolean
  tempId?: string
}

export function OrientationSetupPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', date: '' })
  const [items, setItems] = useState<Item[]>([])
  const [newItemText, setNewItemText] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'กรุณากรอกชื่องาน'
    if (!form.date) errs.date = 'กรุณาเลือกวันที่'
    if (items.length === 0) errs.items = 'กรุณาเพิ่มรายการอย่างน้อย 1 รายการ'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function addItem(category: (typeof ORIENTATION_CATEGORIES)[number]) {
    const text = (newItemText[category] || '').trim()
    if (!text) return
    setItems((prev) => [
      ...prev,
      { name: text, category, isNew: true, tempId: crypto.randomUUID() },
    ])
    setNewItemText((prev) => ({ ...prev, [category]: '' }))
  }

  function removeItem(item: Item) {
    if (item.tempId) {
      setItems((prev) => prev.filter((i) => i.tempId !== item.tempId))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setIsSubmitting(true)

    try {
      // Create event
      const eventRes = await fetch(apiUrl('/api/orientation/events'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), date: form.date }),
      })
      const eventData = await eventRes.json()
      if (!eventData.success) {
        showToast(eventData.error?.messageTh || 'เกิดข้อผิดพลาด')
        setIsSubmitting(false)
        return
      }

      const eventId = eventData.data.id

      // Add items
      const itemRes = await fetch(apiUrl(`/api/orientation/events/${eventId}/items`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((i) => ({ name: i.name, category: i.category })),
        }),
      })
      const itemData = await itemRes.json()
      if (!itemData.success) {
        showToast(itemData.error?.messageTh || 'สร้างงานแล้ว แต่เพิ่มรายการไม่สำเร็จ')
        setIsSubmitting(false)
        return
      }

      showToast('สร้างงานปฐมนิเทศสำเร็จ')
      setTimeout(() => navigate(`/orientation/${eventId}`), 800)
    } catch {
      showToast('เกิดข้อผิดพลาด')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-600">⟨</button>
          <h1 className="text-lg font-bold text-gray-900">สร้างงานปฐมนิเทศ</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Event name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">ชื่องาน *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="เช่น ปฐมนิเทศ ป.3 ปีการศึกษา 2569"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.name ? 'border-red-300' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none`}
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>

          {/* Date */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">วันที่ *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.date ? 'border-red-300' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none`}
            />
            {errors.date && <p className="mt-1 text-xs text-red-500">{errors.date}</p>}
          </div>

          {/* Items by category */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              รายการตรวจสอบ *
            </label>
            {errors.items && <p className="mb-2 text-xs text-red-500">{errors.items}</p>}

            {ORIENTATION_CATEGORIES.map((cat) => {
              const catItems = items.filter((i) => i.category === cat)
              return (
                <div key={cat} className="mb-3 rounded-lg bg-white p-3 shadow-sm">
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">{cat}</h3>

                  {/* Existing items for this category */}
                  {catItems.length > 0 && (
                    <div className="mb-2 space-y-1">
                      {catItems.map((item) => (
                        <div
                          key={item.tempId || item.id}
                          className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-1.5 text-sm"
                        >
                          <span className="text-gray-700">{item.name}</span>
                          <button
                            type="button"
                            onClick={() => removeItem(item)}
                            className="text-red-400 hover:text-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline add */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newItemText[cat] || ''}
                      onChange={(e) =>
                        setNewItemText((prev) => ({ ...prev, [cat]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addItem(cat)
                        }
                      }}
                      placeholder={`เพิ่มรายการ${cat}...`}
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => addItem(cat)}
                      className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white"
                    >
                      +
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'สร้างงานปฐมนิเทศ'}
          </button>
        </form>
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="more" />
    </div>
  )
}
